import {getMonolithApiHeaders} from '@udemy-v2/api-fetch';

import {CourseAccessQueryParam} from '@/features/course-access/constants';

const SEND_COURSE_INVITATION_REQUEST_PATH = '/message/send-course-invitation-request/';

const COURSE_ID_QUERY_PARAM = 'courseId';

export interface BuildSendCourseInvitationRequestUrlParams {
    /** Course id forwarded as the `courseId` query param. */
    courseId: string | number;
    /**
     * Existing search params from the user's current URL. Every param is
     * carried through to the invitation endpoint so the monolith can decide
     * how to redirect back, then `courseId` is set/overwritten.
     */
    searchParams?: URLSearchParams | null;
}

/**
 * Builds the monolith URL that records an invitation request for a private
 * course. Exposed separately so callers can inspect the constructed URL
 * (mainly for tests).
 */
export function buildSendCourseInvitationRequestUrl({
    courseId,
    searchParams,
}: BuildSendCourseInvitationRequestUrlParams): string {
    const params = new URLSearchParams(searchParams?.toString() ?? '');
    params.set(COURSE_ID_QUERY_PARAM, String(courseId));
    return `${SEND_COURSE_INVITATION_REQUEST_PATH}?${params.toString()}`;
}

export type SendCourseInvitationRequestParams = BuildSendCourseInvitationRequestUrlParams;

export interface SendCourseInvitationRequestResult {
    ok: boolean;
    status: number;
    /** True when the monolith responded with a 3xx and the page is being reloaded. */
    redirected: boolean;
}

/**
 * Sends an invitation request for the given course. The monolith view is a
 * `RedirectView` that always responds 302 → CLP and stashes a session message
 * the v1 CLP renders. We can't read that session, but we *can* re-enter the
 * limited-access SSR variant by adding an empty `password=` query param to
 * the current URL — the CF Worker that sits in front of this app sees the
 * `password` query param and routes the next page load to the monolith,
 * which serves the appropriate UI (see `reloadWithEmptyPasswordParam` below).
 *
 * Uses `GET` because the underlying Django view (`CourseInvitationRequestView`)
 * inherits `RedirectView` and only implements `get_redirect_url`. The HTML
 * body of the eventual redirect target is discarded.
 *
 * Routing: the path is on the monolith (no `/api-2.0/` prefix), so in
 * production Cloudflare / API Gateway forwards it directly. There is no
 * matching rewrite for local dev — a proxy hop would rely on the dev
 * monolith reliably exposing this `RedirectView` for arbitrary test users,
 * which it doesn't. Locally this flow is exercised via mocked responses.
 */
export async function sendCourseInvitationRequest({
    courseId,
    searchParams,
}: SendCourseInvitationRequestParams): Promise<SendCourseInvitationRequestResult> {
    const url = buildSendCourseInvitationRequestUrl({courseId, searchParams});

    const response = await fetch(url, {
        method: 'GET',
        redirect: 'manual',
        headers: {
            ...getMonolithApiHeaders(),
        },
    });

    // `redirect: 'manual'` surfaces same-origin 3xx responses as an
    // opaqueredirect (type 'opaqueredirect', status 0). A non-opaque 3xx
    // status is also treated as a redirect for defensive parity with any
    // proxy that forwards the raw status.
    const isRedirect =
        response.type === 'opaqueredirect' ||
        (response.status >= 300 && response.status < 400);

    if (isRedirect) {
        reloadWithEmptyPasswordParam();
        return {ok: true, status: response.status, redirected: true};
    }

    const isSuccess = response.status >= 200 && response.status < 300;
    if (!isSuccess) {
        throw new Error(
            `Failed to send course invitation request: ${response.status} ${response.statusText}`,
        );
    }

    return {ok: true, status: response.status, redirected: false};
}

/**
 * !!! HACK HACK HACK HACK !!!
 * This is a temporary workaround to enable users without password or xCourseControlId query params can be routed back to
 * the monolith. As it stands today, users who have those query params in their requests are routed back to the monolith by default.
 * But no such workaround exists for users who don't include these query params in their initial request to the CLP.
 *
 * Due to the limitations of the CRS access control mechanism, the resolvers in api-platform-graphql cannot fetch course curriculum because there
 * is not a mechanism to check for a valid course invite or to propagate a valid course password through
 * all the RPC calls that fan out to gather course curriculum. Because of this, we cannot render CLPs
 * for limited access courses today, BUT we can determine whether or not a given course is password-protected
 * or invite-only, and we can show the appropriate UIs to users who land on these CLPs. Then, we can
 * use client-side logic to redirect users back to the monolith for validation and display of the CLP
 * once the user:
 * 1) Enters a password  -> password query param added to URL + refresh, CF worker re-routes user to monolith, access granted or denied in monolith
 * 2) Requests an invite (even if they already have an in-flight invite request) -> redirect to monolith with empty password query param,
 *    monolith checks for in-flight invite request and either grants access to CLP or shows message that invite request is pending.
 *
 * This workaround + the existing routing changes already in place for requests that include password and xCourseControlId query params
 * should at least allow users to access limited access CLPs while we build out the remaining access control
 * logic in CRS.
 *
 *
 * **/
function reloadWithEmptyPasswordParam(): void {
    if (typeof window === 'undefined') {
        return;
    }
    const next = new URL(window.location.href);
    next.searchParams.set(CourseAccessQueryParam.PASSWORD, '');
    window.location.replace(next.toString());
}
